import React, { useEffect, useState } from "react";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";
import { Spin, Empty } from "antd";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

export default function Statistiques() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [visiteData, setVisiteData] = useState([]);
  const [memberData, setMemberData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [memberCount, setMemberCount] = useState(0);
  const [totalHours, setTotalHours] = useState(0);
  const [visiteCount, setVisiteCount] = useState(0);
  const [averageHoursPerVisit, setAverageHoursPerVisit] = useState(0);
  const [mostActiveDay, setMostActiveDay] = useState("N/A");
  const [dark, setDark] = useState(() => localStorage.getItem("ws-theme") === "dark");

  const toggleTheme = () => {
    setDark((d) => {
      localStorage.setItem("ws-theme", !d ? "dark" : "light");
      return !d;
    });
  };

  const t = dark ? theme.dark : theme.light;

  useEffect(() => {
    const fetchStats = async () => {
      if (!user?.uid) return;
      const uid = user.uid;
      try {
        const [visiteurSnap, membersSnap] = await Promise.all([
          getDocs(query(collection(db, "visiteur"), where("FirebaseId", "==", uid))),
          getDocs(query(collection(db, "members"), where("FirebaseId", "==", uid))),
        ]);

        const dailyHours = {};
        let maxHours = 0;
        let maxDay = "";
        visiteurSnap.forEach((doc) => {
          const d = doc.data();
          if (d.startTime && d.hours) {
            const dateKey = dayjs(d.startTime.toDate()).format("YYYY-MM-DD");
            dailyHours[dateKey] = (dailyHours[dateKey] || 0) + Number(d.hours);
            if (dailyHours[dateKey] > maxHours) {
              maxHours = dailyHours[dateKey];
              maxDay = dayjs(dateKey).format("DD/MM/YYYY");
            }
          }
        });

        const formatted = Object.entries(dailyHours)
          .map(([date, hours]) => ({ date: dayjs(date).format("DD/MM"), fullDate: date, hours: Math.round(hours * 10) / 10 }))
          .sort((a, b) => new Date(a.fullDate) - new Date(b.fullDate));

        setVisiteData(formatted);
        const total = Object.values(dailyHours).reduce((s, h) => s + h, 0);
        setTotalHours(Math.round(total * 10) / 10);
        setVisiteCount(visiteurSnap.size);
        setAverageHoursPerVisit(visiteurSnap.size > 0 ? Math.round((total / visiteurSnap.size) * 10) / 10 : 0);
        setMostActiveDay(maxDay || "N/A");

        const countByHours = {};
        membersSnap.forEach((doc) => {
          const h = doc.data().Hours;
          if (h) countByHours[h] = (countByHours[h] || 0) + 1;
        });
        const formattedMember = Object.entries(countByHours)
          .map(([hours, count]) => ({ hours: `${hours}h`, hoursNum: parseInt(hours), count }))
          .sort((a, b) => a.hoursNum - b.hoursNum);
        setMemberData(formattedMember);
        setMemberCount(membersSnap.size);
      } catch (err) {
        console.error("Erreur chargement stats:", err);
      }
      setLoading(false);
    };
    fetchStats();
  }, [user]);

  const BAR_COLORS = ["#2563eb", "#378ADD", "#85B7EB", "#1d4ed8", "#3b82f6", "#60a5fa"];

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div style={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: 10, padding: "10px 14px" }}>
        <p style={{ margin: 0, fontSize: 12, fontWeight: 500, color: t.text1, marginBottom: 4 }}>{label}</p>
        {payload.map((entry, i) => (
          <p key={i} style={{ margin: 0, fontSize: 12, color: entry.color }}>
            {entry.name}: <strong>{entry.value}</strong>
          </p>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ background: t.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <Spin size="large" />
          <p style={{ color: t.text2, marginTop: 12, fontSize: 14 }}>Chargement des statistiques...</p>
        </div>
      </div>
    );
  }

  const activityStatus = visiteCount > 10 ? "Très actif" : visiteCount > 5 ? "Actif" : "Modéré";
  const showAchievements = visiteCount > 5 || totalHours > 20;

  return (
    <div style={{ background: t.bg, minHeight: "100vh", paddingBottom: 40, maxWidth: 480, margin: "0 auto" }}>

      {/* Topbar */}
      <div style={s.topbar}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <img src="/logo.PNG" alt="WeStudy" style={{ height: 36, objectFit: "contain" }} />
          <span style={{ fontSize: 11, color: t.text3 }}>Statistiques</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button onClick={toggleTheme} style={{ ...s.toggleTrack, background: t.bg3, border: `1px solid ${t.border}` }}>
            <div style={{ ...s.toggleKnob, background: t.blue, transform: dark ? "translateX(16px)" : "translateX(0)" }} />
          </button>
          <button onClick={() => navigate("/")} style={{ ...s.backBtn, background: t.bg3, border: `1px solid ${t.border}`, color: t.text2 }}>
            ← Retour
          </button>
        </div>
      </div>

      {/* Hero */}
      <div style={{ padding: "20px 16px 16px" }}>
        <div style={{ fontSize: 20, fontWeight: 600, color: t.text1 }}>Tableau de bord 📊</div>
        <div style={{ fontSize: 12, color: t.text2, marginTop: 3 }}>Suivez votre progression et activité</div>
      </div>

      {/* Stat cards */}
      <div style={s.grid2}>
        <StatCard icon="⏱️" value={`${totalHours}h`} label="Total d'heures" color={t.blue} bg={t.blueBg} progress={Math.min((totalHours / 100) * 100, 100)} t={t} />
        <StatCard icon="📅" value={visiteCount} label="Visites" color={t.green} bg={t.greenBg} progress={Math.min((visiteCount / 50) * 100, 100)} t={t} />
        <StatCard icon="👥" value={memberCount} label="Abonnements" color={t.amber} bg={t.amberBg} progress={Math.min((memberCount / 10) * 100, 100)} t={t} />
        <StatCard icon="📈" value={`${averageHoursPerVisit}h`} label="Moy. / visite" color={t.purple} bg={t.purpleBg} progress={Math.min((averageHoursPerVisit / 8) * 100, 100)} t={t} />
      </div>

      {/* Area chart */}
      <div style={{ padding: "0 16px", marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 500, color: t.text3, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10 }}>Activité journalière</div>
        <div style={{ ...s.card, background: t.card, border: `1px solid ${t.border}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <span style={{ fontSize: 13, fontWeight: 500, color: t.text1 }}>Heures par jour</span>
            <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, background: t.blueBg, color: t.blueText }}>{visiteData.length} jours</span>
          </div>
          {visiteData.length === 0 ? (
            <Empty description={<span style={{ color: t.text2, fontSize: 12 }}>Aucune donnée disponible</span>} image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <ResponsiveContainer width="100%" height={160}>
              <AreaChart data={visiteData}>
                <defs>
                  <linearGradient id="blueGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={t.blue} stopOpacity={0.2} />
                    <stop offset="95%" stopColor={t.blue} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={t.border} />
                <XAxis dataKey="date" stroke={t.text3} tick={{ fontSize: 10, fill: t.text3 }} />
                <YAxis stroke={t.text3} tick={{ fontSize: 10, fill: t.text3 }} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="hours" stroke={t.blue} strokeWidth={2} fill="url(#blueGrad)" name="Heures" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Bar chart */}
      <div style={{ padding: "0 16px", marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 500, color: t.text3, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10 }}>Abonnements</div>
        <div style={{ ...s.card, background: t.card, border: `1px solid ${t.border}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <span style={{ fontSize: 13, fontWeight: 500, color: t.text1 }}>Répartition par formule</span>
            <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, background: t.greenBg, color: t.greenText }}>{memberCount} abonnement{memberCount > 1 ? "s" : ""}</span>
          </div>
          {memberData.length === 0 ? (
            <Empty description={<span style={{ color: t.text2, fontSize: 12 }}>Aucun abonnement enregistré</span>} image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={memberData}>
                <CartesianGrid strokeDasharray="3 3" stroke={t.border} />
                <XAxis dataKey="hours" stroke={t.text3} tick={{ fontSize: 10, fill: t.text3 }} />
                <YAxis stroke={t.text3} tick={{ fontSize: 10, fill: t.text3 }} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" name="Abonnements" radius={[6, 6, 0, 0]} barSize={36}>
                  {memberData.map((_, i) => (
                    <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Info row */}
      <div style={{ ...s.grid2, marginBottom: 16 }}>
        <div style={{ ...s.infoCard, background: t.card, border: `1px solid ${t.border}` }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: t.amberBg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>🏆</div>
          <div>
            <div style={{ fontSize: 10, color: t.text2 }}>Jour le plus actif</div>
            <div style={{ fontSize: 13, fontWeight: 500, color: t.text1, marginTop: 2 }}>{mostActiveDay}</div>
          </div>
        </div>
        <div style={{ ...s.infoCard, background: t.card, border: `1px solid ${t.border}` }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: t.blueBg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>⚡</div>
          <div>
            <div style={{ fontSize: 10, color: t.text2 }}>Statut</div>
            <div style={{ fontSize: 13, fontWeight: 500, color: t.green, marginTop: 2 }}>{activityStatus}</div>
          </div>
        </div>
      </div>

      {/* Achievements */}
      {showAchievements && (
        <div style={{ padding: "0 16px" }}>
          <div style={{ background: t.greenBg, border: `1px solid ${t.border}`, borderRadius: 14, padding: "20px 16px", textAlign: "center" }}>
            <div style={{ fontSize: 28, marginBottom: 6 }}>🎉</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: t.greenText }}>Félicitations !</div>
            <div style={{ fontSize: 12, color: t.text2, marginTop: 4 }}>Vous êtes un membre actif de notre communauté</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "center", marginTop: 12 }}>
              {totalHours >= 50 && <span style={{ fontSize: 11, padding: "4px 10px", borderRadius: 20, background: t.amberBg, color: t.amberText }}>🏆 +50h d'étude</span>}
              {visiteCount >= 10 && <span style={{ fontSize: 11, padding: "4px 10px", borderRadius: 20, background: t.blueBg, color: t.blueText }}>⭐ Visiteur régulier</span>}
              {memberCount >= 3 && <span style={{ fontSize: 11, padding: "4px 10px", borderRadius: 20, background: t.purpleBg, color: t.purpleText }}>💎 Membre fidèle</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon, value, label, color, bg, progress, t }) {
  return (
    <div style={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: 14, padding: 14 }}>
      <div style={{ width: 32, height: 32, borderRadius: 8, background: bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, marginBottom: 10 }}>{icon}</div>
      <div style={{ fontSize: 24, fontWeight: 600, color, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 11, color: t.text2, marginTop: 3 }}>{label}</div>
      <div style={{ height: 3, borderRadius: 3, background: t.bg3, marginTop: 10, overflow: "hidden" }}>
        <div style={{ height: "100%", borderRadius: 3, background: color, width: `${progress}%` }} />
      </div>
    </div>
  );
}

const theme = {
  light: {
    bg: "#f8fafc", bg3: "#f1f5f9", border: "#e2e8f0",
    text1: "#0f172a", text2: "#64748b", text3: "#94a3b8",
    blue: "#2563eb", blueBg: "#eff6ff", blueText: "#1d4ed8",
    green: "#16a34a", greenBg: "#f0fdf4", greenText: "#15803d",
    amber: "#d97706", amberBg: "#fffbeb", amberText: "#b45309",
    purple: "#7c3aed", purpleBg: "#f5f3ff", purpleText: "#6d28d9",
    card: "#ffffff",
  },
  dark: {
    bg: "#0f172a", bg3: "#1a2740", border: "#273549",
    text1: "#f1f5f9", text2: "#94a3b8", text3: "#475569",
    blue: "#378ADD", blueBg: "#1e3a5f", blueText: "#85B7EB",
    green: "#4ade80", greenBg: "#052e16", greenText: "#86efac",
    amber: "#fbbf24", amberBg: "#2a1e07", amberText: "#fcd34d",
    purple: "#a78bfa", purpleBg: "#1e1244", purpleText: "#c4b5fd",
    card: "#1e293b",
  },
};

const s = {
  topbar: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 16px 0" },
  toggleTrack: { width: 36, height: 20, borderRadius: 20, cursor: "pointer", position: "relative", flexShrink: 0 },
  toggleKnob: { width: 14, height: 14, borderRadius: "50%", position: "absolute", top: 2, left: 2, transition: "transform 0.2s" },
  backBtn: { fontSize: 12, borderRadius: 8, padding: "5px 10px", cursor: "pointer" },
  grid2: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 10, padding: "0 16px", marginBottom: 16 },
  card: { borderRadius: 14, padding: 16 },
  infoCard: { borderRadius: 14, padding: 14, display: "flex", alignItems: "center", gap: 10 },
};